import { Heart, Users } from "lucide-react";
import React from "react";
import { motion } from "framer-motion";
import Modal from "../../../components/common/Modal";
import VoterAvatars from "../VoterAvatars";
import { TRAVEL_CATEGORIES, useTravelCategories } from "../constants";
import { useTranslation } from "react-i18next";

const IdeaDetailModal = ({ selectedIdea, onClose, handleVote }) => {
  const { t } = useTranslation();
  const categories = useTravelCategories();

  // Find the category label from the categories array
  const getCategoryLabel = () => {
    const category = categories.find(
      (cat) => cat.value === selectedIdea.category
    );
    return category?.label || t("ideas.detail.uncategorized");
  };

  const headerContent = (
    <div>
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.4 }}
        className="tw-text-2xl tw-font-bold tw-bg-gradient-to-r tw-from-gray-900 tw-to-gray-600 tw-bg-clip-text tw-text-transparent"
      >
        {selectedIdea.destination}
      </motion.h2>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="tw-flex tw-items-center tw-space-x-2 tw-mt-2"
      >
        <span className="tw-text-sm tw-text-gray-500">
          {t("ideas.detail.posted_by")}{" "}
          {selectedIdea.author || t("ideas.detail.anonymous")}
        </span>
        <span className="tw-text-gray-300">•</span>
        <span className="tw-px-3 tw-py-1 tw-rounded-full tw-bg-gray-100 tw-text-xs tw-font-medium tw-text-gray-600">
          {getCategoryLabel()}
        </span>
      </motion.div>
    </div>
  );

  return (
    <Modal onClose={onClose} headerContent={headerContent}>
      {selectedIdea.images && selectedIdea.images.length > 0 && (
        <div className="tw-mb-6 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
          {selectedIdea.images.map((image, index) => (
            <div
              key={index}
              className="tw-rounded-xl tw-overflow-hidden tw-h-64"
            >
              <img
                src={image}
                alt={`${selectedIdea.destination} - Image ${index + 1}`}
                className="tw-w-full tw-h-full tw-object-cover"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.4 }}
        className="tw-mb-8 tw-pr-2"
      >
        {selectedIdea.description ? (
          <p className="tw-text-gray-600 tw-whitespace-pre-line">
            {selectedIdea.description}
          </p>
        ) : (
          <p className="tw-text-gray-400 tw-italic">
            {t("ideas.detail.no_description")}
          </p>
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.4 }}
        className="tw-flex tw-flex-col tw-space-y-4"
      >
        <div className="tw-flex tw-justify-between tw-items-center">
          <div className="tw-flex tw-items-center tw-space-x-4">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => handleVote(selectedIdea.id)}
              className="tw-flex tw-items-center tw-space-x-2 tw-px-4 tw-py-2 tw-rounded-full tw-bg-rose-50 hover:tw-bg-rose-100 tw-transition-colors"
            >
              <Heart
                className={`tw-w-5 tw-h-5 ${
                  selectedIdea.hasVoted
                    ? "tw-fill-rose-500 tw-text-rose-500"
                    : "tw-text-rose-500"
                }`}
              />
              <span className="tw-font-medium tw-text-rose-500">
                {selectedIdea.votes} {t("ideas.detail.votes")}
              </span>
            </motion.button>
          </div>
        </div>

        {selectedIdea.voters && selectedIdea.voters.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="tw-bg-gray-50 tw-rounded-xl tw-p-4"
          >
            <div className="tw-flex tw-items-center tw-space-x-3 tw-mb-3">
              <Users className="tw-w-5 tw-h-5 tw-text-gray-500" />
              <span className="tw-font-medium tw-text-gray-700">
                {t("ideas.detail.supporters")}
              </span>
            </div>
            <VoterAvatars voters={selectedIdea.voters} maxDisplay={8} />
            {selectedIdea.voters.length > 8 && (
              <p className="tw-text-sm tw-text-gray-500 tw-mt-2">
                {t("ideas.detail.more_supporters", {
                  count: selectedIdea.voters.length - 8,
                })}
              </p>
            )}
          </motion.div>
        )}
      </motion.div>
    </Modal>
  );
};

export default IdeaDetailModal;
